import React, { useState, useCallback } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
  IonCheckbox,
  IonSpinner,
} from '@ionic/react';
import { arrowBack, printOutline, downloadOutline } from 'ionicons/icons';
import { ReportRow, ReportDataSource, InspectionResultRow, reportingService } from '@/services/ReportingService';
import {
  reportGenerationService,
  ReportOptions,
  DEFAULT_REPORT_OPTIONS,
} from '@/services/ReportGenerationService';
import ModalFooter from '@/components/common/ModalFooter';

interface Props {
  isOpen: boolean;
  selectedRows: ReportRow[];
  dataSource: ReportDataSource;
  onClose: () => void;
}

const OPTION_LABELS: { key: keyof ReportOptions; label: string; description: string }[] = [
  { key: 'lineItemDetail', label: 'Line Item Detail', description: 'Audit type, area, and line item attachments' },
  { key: 'background', label: 'Inspection Plan Background', description: 'Weights, method, sample size, and plan attachments' },
  { key: 'frequency', label: 'Frequency', description: 'Fixed inspection dates or auto-adjusted frequencies' },
  { key: 'scoringCriteria', label: 'Scoring Criteria for Checklist Items', description: 'Scoring Criteria text per checklist item' },
  { key: 'checklistComments', label: 'Checklist Comments', description: 'Inspector comments on individual checklist items' },
  { key: 'attachmentDescription', label: 'Attachments with Preview', description: 'Image previews, icons, filenames, and descriptions' },
];

const GenerateReportModal: React.FC<Props> = ({ isOpen, selectedRows, dataSource, onClose }) => {
  const [options, setOptions] = useState<ReportOptions>({ ...DEFAULT_REPORT_OPTIONS });
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState('inspection-report');
  const [filenameError, setFilenameError] = useState<string | null>(null);

  const isInspectionResults = dataSource === 'inspectionResults';
  const inspectionResultIds = isInspectionResults
    ? selectedRows.map((r) => (r as InspectionResultRow).inspectionResultId)
    : [];

  const toggleOption = useCallback((key: keyof ReportOptions) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const selectAll = useCallback(() => {
    setOptions({
      lineItemDetail: true,
      background: true,
      frequency: true,
      scoringCriteria: true,
      checklistComments: true,
      attachmentDescription: true,
    });
  }, []);

  const clearAll = useCallback(() => {
    setOptions({
      lineItemDetail: false,
      background: false,
      frequency: false,
      scoringCriteria: false,
      checklistComments: false,
      attachmentDescription: false,
    });
  }, []);

  const handleGenerate = useCallback(async () => {
    if (inspectionResultIds.length === 0) {
      setError('No inspection results selected. Generate Report is only available for Inspection Results.');
      return;
    }
    setGenerating(true);
    setError(null);
    setProgress('Starting…');
    try {
      await reportGenerationService.generateReport(inspectionResultIds, options, (msg) => setProgress(msg));
      setProgress('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
      setProgress('');
    } finally {
      setGenerating(false);
    }
  }, [inspectionResultIds, options, onClose]);

  const handleDownloadPdf = useCallback(async () => {
    if (inspectionResultIds.length === 0) {
      setError('No inspection results selected. Generate Report is only available for Inspection Results.');
      return;
    }
    const fnameErr = reportingService.validateFilename(filename);
    if (fnameErr) {
      setFilenameError(fnameErr);
      return;
    }
    setGenerating(true);
    setError(null);
    setFilenameError(null);
    setProgress('Starting…');
    try {
      const pdfFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      await reportGenerationService.downloadReportAsPdf(inspectionResultIds, options, pdfFilename, (msg) => setProgress(msg));
      setProgress('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF');
      setProgress('');
    } finally {
      setGenerating(false);
    }
  }, [inspectionResultIds, options, filename]);

  const handleDismiss = () => {
    if (!generating) {
      setError(null);
      setProgress('');
      setFilenameError(null);
      onClose();
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleDismiss}
      style={{
        '--width': '640px',
        '--min-width': '480px',
        '--height': '65vh',
        '--max-height': '85vh',
        '--border-radius': '12px',
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Generate Report</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '20px 24px' }}>
          {/* Summary */}
          <div style={{
            background: 'var(--ion-color-light-tint)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            border: '1px solid var(--ion-color-light-shade)',
          }}>
            <IonText style={{ fontSize: '0.9rem', color: 'var(--ion-color-dark)' }}>
              <strong>{inspectionResultIds.length}</strong> inspection result{inspectionResultIds.length !== 1 ? 's' : ''} selected
              {inspectionResultIds.length > 0 && (
                <span style={{ color: '#666', marginLeft: '8px', fontSize: '0.82rem' }}>
                  (IDs: {inspectionResultIds.slice(0, 10).join(', ')}{inspectionResultIds.length > 10 ? `, … +${inspectionResultIds.length - 10} more` : ''})
                </span>
              )}
            </IonText>
          </div>

          {/* Not available for plans */}
          {!isInspectionResults && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '0.85rem',
              color: '#856404',
            }}>
              ⚠ Report generation is only available for <strong>Inspection Results</strong>. Please switch the data source to Inspection Results on the Reporting page.
            </div>
          )}

          {/* Options */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <IonText style={{ fontWeight: '600', fontSize: '0.95rem', color: 'var(--ion-color-dark)' }}>
                Report Sections
              </IonText>
              <div style={{ display: 'flex', gap: '6px' }}>
                <IonButton fill="clear" size="small" onClick={selectAll} disabled={generating} style={{ fontSize: '0.78rem' }}>
                  Select All
                </IonButton>
                <IonButton fill="clear" size="small" onClick={clearAll} disabled={generating} style={{ fontSize: '0.78rem' }}>
                  Clear All
                </IonButton>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {OPTION_LABELS.map(({ key, label, description }) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: generating ? 'default' : 'pointer',
                    background: options[key] ? '#eef2ff' : 'transparent',
                    border: `1px solid ${options[key] ? '#b8c8f0' : 'transparent'}`,
                    transition: 'all 0.15s ease',
                  }}
                  onClick={() => !generating && toggleOption(key)}
                >
                  <IonCheckbox
                    checked={options[key]}
                    disabled={generating}
                    style={{ marginTop: '2px', '--size': '18px' }}
                    onIonChange={() => {/* handled by label click */}}
                  />
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '0.88rem', color: '#333' }}>{label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#777', marginTop: '1px' }}>{description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <IonText color="medium" style={{ fontSize: '0.75rem', display: 'block', marginTop: '8px' }}>
            Checklist Items and Inspection Result Details are always included in the report.
          </IonText>

          {/* Filename for PDF download */}
          <div style={{ marginTop: '16px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444', display: 'block', marginBottom: '6px' }}>
              PDF Filename
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="text"
                value={filename}
                onChange={(e) => { setFilename(e.target.value); setFilenameError(null); }}
                placeholder="inspection-report"
                disabled={generating}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  fontSize: '1rem',
                  border: filenameError ? '2px solid var(--ion-color-danger)' : '2px solid #888',
                  borderRadius: '6px',
                  outline: 'none',
                  background: '#fff',
                  color: '#000',
                  fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500' }}>.pdf</span>
            </div>
            {filenameError && (
              <div style={{ fontSize: '0.78rem', color: 'var(--ion-color-danger)', marginTop: '4px' }}>
                {filenameError}
              </div>
            )}
          </div>

          {/* Progress */}
          {generating && progress && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px',
              padding: '10px 14px',
              background: '#eef6ff',
              borderRadius: '6px',
              border: '1px solid #b3d4fc',
            }}>
              <IonSpinner name="crescent" style={{ width: '18px', height: '18px' }} />
              <IonText style={{ fontSize: '0.85rem', color: '#2c5282' }}>{progress}</IonText>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: '#fff5f5',
              borderRadius: '6px',
              border: '1px solid #feb2b2',
              fontSize: '0.85rem',
              color: '#c53030',
            }}>
              {error}
            </div>
          )}
        </div>
      </IonContent>

      <ModalFooter
        cancelLabel="Cancel"
        actionLabel={generating ? 'Generating...' : 'Print Preview'}
        cancelIcon={arrowBack}
        actionIcon={printOutline}
        onCancel={handleDismiss}
        onAction={handleGenerate}
        isCancelDisabled={generating}
        isActionDisabled={generating || !isInspectionResults || inspectionResultIds.length === 0}
        isActionLoading={generating}
        actionColor="primary"
        cancelFill="outline"
        secondaryAction={{
          label: generating ? 'Downloading...' : 'Download PDF',
          onClick: handleDownloadPdf,
          disabled: generating || !isInspectionResults || inspectionResultIds.length === 0,
          color: 'tertiary',
          icon: downloadOutline,
        }}
      />
    </IonModal>
  );
};

export default GenerateReportModal;
